import {
  ChangeDetectionStrategy,
  Component,
  HostBinding,
  computed,
  effect,
  inject,
} from '@angular/core';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { TooltipDirective } from '../../../../shared/directives/tooltip.directive';
import {
  lucideCheck,
  lucideRotateCcw,
  lucideWand2,
} from '@ng-icons/lucide';

import { RecorderStore } from '../../state/recorder.store';
import type { AutoMicGainState } from '../../state/recorder.types';

/**
 * AutoMicGainComponent — UI shell for the Auto Mic Gain workflow.
 *
 * State is owned entirely by RecorderStore. Actual calibration is driven by
 * SuperrecorderComponent via the recorder engine (startAutoCalibration /
 * applyBoostChain / removeBoostChain). This component only renders store
 * state and dispatches toggle/recalibrate actions.
 */
@Component({
  selector: 'rec-auto-mic-gain',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, TooltipDirective],
  providers: [provideIcons({ lucideCheck, lucideRotateCcw, lucideWand2 })],
  templateUrl: './auto-mic-gain.component.html',
  styleUrls: ['./auto-mic-gain.component.scss'],
})
export class AutoMicGainComponent {
  readonly store = inject(RecorderStore);

  @HostBinding('attr.data-state')
  get stateAttr(): AutoMicGainState {
    return this.store.autoMicGain();
  }

  readonly progressPct = computed(() =>
    Math.round(this.store.autoMicGainProgress() * 100)
  );

  readonly bestLevelLabel = computed(() => {
    const v = this.store.bestLevelDb();
    if (v === null) return '';
    const sign = v >= 0 ? '+' : '';
    return `${sign}${Math.round(v)} dB`;
  });

  readonly disabled = computed(
    () => this.store.controlLocks().autoMicGain
  );

  readonly enabled = computed(() => this.store.autoMicGain() !== 'off');

  constructor() {
    // Abort calibration if controls lock mid-run (e.g. recording starts).
    effect(() => {
      if (this.disabled() && this.store.autoMicGain() === 'calculating') {
        this.cancel();
      }
    });
  }

  onToggle(): void {
    if (this.disabled()) return;
    if (this.enabled()) {
      this.store.disableAutoMicGain();
      this.announce('Auto Mic Gain off.');
    } else {
      this.store.enableAutoMicGain();
      this.announce('Auto Mic Gain on. Calibrating.');
    }
  }

  recalibrate(): void {
    if (this.disabled()) return;
    this.store.recalibrateAutoMicGain();
    this.announce('Recalibrating microphone.');
  }

  cancel(): void {
    if (this.store.bestLevelDb() !== null) {
      this.store.settleAutoMicGain();
    } else {
      this.store.disableAutoMicGain();
    }
  }

  private announce(msg: string): void {
    this.store.announce.set(msg);
  }
}
